import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { getLocalized } from '../../store';

/**
 * Lookbook-style product card with hover overlay + optional wishlist heart.
 */
export const ProductCard = ({
  product,
  language,
  categoryLabel,
  ctaLabel,
  isAuthenticated,
  isInWishlist,
  onWishlistToggle,
  index = 0,
}) => {
  const primaryUrl = product.images?.[0]?.url;
  const localizedName = getLocalized(product.name, language);

  // 🎯 Проверяем, относится ли этот предмет к категории кастомного производства
  const isCustomFurniture =
    product.category === 'custom_furniture' ||
    product.category === 'custom-furniture' ||
    product.category === 'maatwerk_meubels';

  // 🎯 Формируем динамический путь назначения в зависимости от категории
  const destinationLink = isCustomFurniture
    ? `/${language}/custom-furniture`
    : `/${language}/products/${product.id}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={destinationLink}
        className="group block relative"
        data-testid={`product-card-${product.id}`}
      >
        <div className="aspect-[4/5] overflow-hidden bg-muted mb-6 relative">
          {primaryUrl ? (
            <img
              src={primaryUrl}
              alt={localizedName}
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="w-full h-full bg-gradient-to-br from-muted to-muted-foreground/10 flex items-center justify-center"
            style={{ display: primaryUrl ? 'none' : 'flex' }}
          >
            <span className="text-muted-foreground text-sm font-medium tracking-wide">
              {localizedName}
            </span>
          </div>

          {/* Hover overlay with CTA */}
          <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors duration-500 flex items-end justify-center pb-8">
            <span className="text-background text-xs tracking-[0.25em] uppercase font-light opacity-0 group-hover:opacity-100 transition-opacity duration-500 border-b border-background/60 pb-1">
              {ctaLabel}
            </span>
          </div>

          {/* Wishlist Button (активируется только для стандартных коммерческих позиций) */}
          {isAuthenticated && !isCustomFurniture && (
            <button
              onClick={(e) => onWishlistToggle(e, product.id)}
              className="absolute top-4 right-4 w-10 h-10 bg-background/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`wishlist-btn-${product.id}`}
            >
              <Heart
                className={`w-5 h-5 ${isInWishlist(product.id) ? 'fill-foreground' : ''}`}
                strokeWidth={1.5}
              />
            </button>
          )}
        </div>

        <div>
          <p className="text-caption text-muted-foreground mb-2">{categoryLabel}</p>
          <h3 className="text-xl md:text-2xl font-extralight tracking-tight group-hover:text-muted-foreground transition-colors duration-500">
            {localizedName}
          </h3>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
